/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { QUERY_GC_TIME } from "@/lib/constants/query-config";
import {
  checkReactQueryCache,
  startMemoryMonitoring,
  stopMemoryMonitoring,
} from "@/lib/performance/memory-monitor";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { useEffect, useMemo } from "react";

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Use standard staleTime as default (individual queries can override)
            // PERFORMANCE: Increased from 5 to 7 minutes to reduce parallel refetches
            staleTime: 7 * 60 * 1000, // 7 minutes (was 5 minutes)
            gcTime: QUERY_GC_TIME.STANDARD, // 2 minutes garbage collection time

            // Enable structural sharing to prevent unnecessary re-renders
            structuralSharing: true,

            // Retry configuration
            retry: (failureCount, error) => {
              // Don't retry authentication errors
              if (
                error?.message?.includes("Authentication") ||
                error?.message?.includes("Unauthorized") ||
                error?.message?.includes("Forbidden")
              ) {
                return false;
              }
              // Retry up to 2 times for other errors
              return failureCount < 2;
            },

            // Refetch behavior optimization
            refetchOnWindowFocus: false, // Don't refetch when window regains focus
            refetchOnReconnect: true, // Refetch when network reconnects

            // Background refetching for critical data
            // NOTE: Realtime subscriptions are preferred - refetchInterval is fallback only
            // Dashboard: Uses realtime (useDashboardRealtime) - refetch disabled
            // Notifications: Uses realtime (useNotifications with realtime:true) - reduced refetch
            // No global background refetching — individual queries opt-in via their own refetchInterval.
            // Realtime subscriptions handle live updates for dashboard/notifications.
            refetchInterval: false,

            // Network mode optimization
            networkMode: "online",

            // Query timeout
            retryDelay: (attemptIndex) =>
              Math.min(1000 * 2 ** attemptIndex, 30000),
          },
          mutations: {
            // Pre-launch audit: mutations should NOT retry by default.
            // POST/create/update operations are not idempotent — retrying
            // a failed insert can produce duplicates (e.g., the 409 storm
            // on client creation). The API client already has its own retry
            // layer for 429s. Let individual mutations opt-in to retries
            // via their own `retry` option if they're truly idempotent.
            retry: false,
          },
        },
      }),
    [],
  );

  // Start memory monitoring (disabled in development to reduce CPU usage)
  useEffect(() => {
    // PERFORMANCE: Skip memory monitoring in development to reduce CPU/memory overhead
    // This monitoring is primarily useful for production debugging
    if (process.env.NODE_ENV === "development") {
      return;
    }

    // Start memory monitoring (checks every 30 seconds)
    startMemoryMonitoring(30000);

    // Monitor React Query cache every 60 seconds
    const cacheMonitorInterval = setInterval(() => {
      checkReactQueryCache(queryClient);
    }, 60000);

    return () => {
      stopMemoryMonitoring();
      clearInterval(cacheMonitorInterval);
    };
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
